import { contentCategory } from "@hexling/schema";
import {
  Button,
  Dialog,
  EmptyState,
  Spinner,
  StatusMessage,
  TextAreaField,
  TextField,
} from "@hexling/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { PageTitle } from "../../components/Layout";
import { Markdown } from "../../components/Markdown";
import { useContent, useServices } from "../../data/AppData";
import type { NoteDraft, Ref } from "../../data/user";
import { localDayKey } from "../../lib/dates";

/** "plant:lavender" -> ref (used when starting a note from a library item). */
export function parseRef(value: string | undefined): Ref | null {
  if (!value) return null;
  const [category, slug] = value.split(":");
  const parsed = contentCategory.safeParse(category);
  return parsed.success && slug ? { category: parsed.data, slug } : null;
}

export function NoteEditorPage({
  id,
  startRef,
  justSaved = false,
}: {
  id: string;
  startRef?: string;
  justSaved?: boolean;
}) {
  const { user } = useServices();
  const navigate = useNavigate();
  const index = useContent().data;
  const isNew = id === "new";
  const existing = useQuery({
    queryKey: ["user", "note", id],
    queryFn: () => (isNew ? null : user.note(id).then((n) => n ?? null)),
  });
  const [draft, setDraft] = useState<NoteDraft | null>(null);
  const [tagText, setTagText] = useState("");
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [status, setStatus] = useState(justSaved ? "Saved" : "");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (draft || existing.isPending) return;
    const n = existing.data;
    const ref = parseRef(startRef);
    const item = ref && index?.get(ref.category, ref.slug);
    setDraft(
      n
        ? { title: n.title, body: n.body, tags: n.tags, refs: n.refs, occurredOn: n.occurredOn }
        : {
            title: item ? `Notes on ${item.name}` : "",
            body: "",
            tags: [],
            refs: ref ? [ref] : [],
            occurredOn: localDayKey(new Date()),
          },
    );
    setTagText(n ? n.tags.join(", ") : "");
  }, [existing.isPending, existing.data, draft, startRef, index]);

  if (existing.isPending || !draft) return <Spinner />;
  if (!isNew && !existing.data)
    return (
      <EmptyState title="Note not found" action={<Link to="/grimoire">Back to your grimoire</Link>}>
        It may have been deleted on another device.
      </EmptyState>
    );

  const save = async () => {
    if (!draft.title.trim()) {
      setError("Give your note a title.");
      return;
    }
    setError(null);
    const tags = tagText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 30);
    const saved = await user.saveNote(
      { ...draft, title: draft.title.trim(), tags },
      isNew ? undefined : id,
    );
    setStatus("Saved");
    if (isNew)
      await navigate({
        to: "/grimoire/notes/$id",
        params: { id: saved.id },
        search: { saved: 1 },
        replace: true,
      });
  };

  const remove = async () => {
    await user.deleteNote(id);
    await navigate({ to: "/grimoire" });
  };

  return (
    <>
      <PageTitle
        title={isNew ? "New note" : draft.title || "Note"}
        back={{ to: "/grimoire", label: "Grimoire" }}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <TextField
          label="Title"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          maxLength={200}
          error={error}
        />
        <TextField
          label="Date"
          type="date"
          value={draft.occurredOn ?? ""}
          onChange={(e) => setDraft({ ...draft, occurredOn: e.target.value || null })}
        />
        <div className="toolbar" role="group" aria-label="Editor mode">
          <Button
            variant={mode === "edit" ? "primary" : "secondary"}
            size="small"
            onClick={() => setMode("edit")}
            aria-pressed={mode === "edit"}
          >
            Write
          </Button>
          <Button
            variant={mode === "preview" ? "primary" : "secondary"}
            size="small"
            onClick={() => setMode("preview")}
            aria-pressed={mode === "preview"}
          >
            Preview
          </Button>
        </div>
        {mode === "edit" ? (
          <TextAreaField
            label="Note"
            hint="Markdown works: **bold**, *italic*, lists and headings."
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            rows={12}
          />
        ) : (
          <div className="hx-panel note-preview">
            {draft.body ? (
              <Markdown source={draft.body} />
            ) : (
              <p className="hx-muted">Nothing written yet.</p>
            )}
          </div>
        )}
        <TextField
          label="Tags"
          hint="Separate with commas"
          value={tagText}
          onChange={(e) => setTagText(e.target.value)}
        />
        {draft.refs.length > 0 && (
          <section aria-labelledby="linked">
            <h2 id="linked">Linked entries</h2>
            <ul className="tag-list">
              {draft.refs.map((r) => {
                const item = index?.get(r.category, r.slug);
                return (
                  <li key={`${r.category}:${r.slug}`}>
                    <Link
                      to="/library/$category/$slug"
                      params={{ category: r.category, slug: r.slug }}
                    >
                      {item?.name ?? r.slug}
                    </Link>{" "}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        setDraft({ ...draft, refs: draft.refs.filter((x) => x !== r) })
                      }
                      aria-label={`Unlink ${item?.name ?? r.slug}`}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        <div className="toolbar">
          <Button type="submit">Save note</Button>
          {!isNew && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete note
            </Button>
          )}
        </div>
        <StatusMessage>{status}</StatusMessage>
      </form>
      <Dialog
        open={confirmDelete}
        title="Delete this note?"
        onClose={() => setConfirmDelete(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={remove}>
              Delete note
            </Button>
          </>
        }
      >
        <p>This removes it from every device that syncs.</p>
      </Dialog>
    </>
  );
}
